import { Link } from 'react-router-dom';
import { Calendar, Clock, Mail, Shield, Users, Zap, ArrowRight, Star, CheckCircle } from 'lucide-react';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import AdSense from '../components/AdSense';

const Home = () => {
  const { t } = useTranslation();
  
  const features = [
    {
      icon: Calendar,
      title: t('home.features.calendar.title'),
      description: t('home.features.calendar.description')
    },
    {
      icon: Mail,
      title: t('home.features.notifications.title'),
      description: t('home.features.notifications.description')
    },
    {
      icon: Shield,
      title: t('home.features.security.title'),
      description: t('home.features.security.description')
    },
    {
      icon: Users,
      title: t('home.features.interface.title'),
      description: t('home.features.interface.description')
    },
    {
      icon: Clock,
      title: t('home.features.availability.title'),
      description: t('home.features.availability.description')
    },
    {
      icon: Zap,
      title: t('home.features.setup.title'),
      description: t('home.features.setup.description')
    }
  ];

  const testimonials = [
    {
      name: 'Marie Dupont',
      role: 'Coiffeuse',
      content: 'EasyRDV m\'a permis de doubler mes rendez-vous en quelques semaines. L\'interface est intuitive et mes clients adorent la simplicité.',
      rating: 5
    },
    {
      name: 'Jean Martin',
      role: 'Coach sportif',
      content: 'Plus besoin de gérer les appels téléphoniques pour les rendez-vous. Tout se fait automatiquement et je gagne un temps fou !',
      rating: 5
    },
    {
      name: 'Sophie Bernard',
      role: 'Thérapeute',
      content: 'Mes clients peuvent prendre rendez-vous même tard le soir. C\'est un vrai plus pour mon activité.',
      rating: 5
    }
  ];

  return (
    <div className="space-y-20">
      {/* Google AdSense Ad Top - Header Banner */}
      <div className="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-center py-4">
          <AdSense 
            slot="1271844633"
            format="horizontal"
            style={{ display: 'block', width: '728px', height: '90px' }}
            className="w-full max-w-4xl"
          />
        </div>
      </div>
      {/* Hero Section */}
      <section className="relative overflow-hidden bg-gradient-to-br from-indigo-50 via-white to-purple-50 py-20">
        <div className="absolute inset-0 bg-grid-pattern opacity-5"></div>
        <div className="relative max-w-6xl mx-auto px-4 text-center">
          <div className="mb-8">
            <div className="inline-flex items-center px-4 py-2 bg-indigo-100 text-indigo-700 rounded-full text-sm font-medium mb-6">
              <Star className="w-4 h-4 mr-2" />
              {t('home.hero.subtitle')}
            </div>
          </div>
          
          <h1 className="text-5xl md:text-7xl font-bold text-gray-900 mb-8 leading-tight">
            {t('home.hero.title')}
          </h1>
          
          <p className="text-xl md:text-2xl text-gray-600 mb-10 max-w-3xl mx-auto leading-relaxed">
            {t('home.subtitle')}
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-12">
            <Link
              to="/register"
              className="group bg-gradient-to-r from-indigo-600 to-purple-600 text-white px-8 py-4 rounded-xl font-semibold hover:from-indigo-700 hover:to-purple-700 transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-xl flex items-center justify-center"
            >
              {t('home.hero.cta')}
              <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </Link>
            <Link
              to="/login"
              className="border-2 border-gray-200 text-gray-700 px-8 py-4 rounded-xl font-semibold hover:border-indigo-300 hover:bg-indigo-50 transition-all duration-300 flex items-center justify-center"
            >
              {t('navigation.login')}
            </Link>
          </div>
          
          <div className="flex items-center justify-center space-x-8 text-sm text-gray-500">
            <div className="flex items-center">
              <CheckCircle className="w-4 h-4 text-green-500 mr-2" />
              {t('home.hero.freeForever')}
            </div>
            <div className="flex items-center">
              <CheckCircle className="w-4 h-4 text-green-500 mr-2" />
              {t('home.hero.setup5min')}
            </div>
            <div className="flex items-center">
              <CheckCircle className="w-4 h-4 text-green-500 mr-2" />
              {t('home.hero.support247')}
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              {t('home.features.title')}
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              {t('home.subtitle')}
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => {
              const Icon = feature.icon;
              return (
                <div key={index} className="group bg-gradient-to-br from-white to-gray-50 p-8 rounded-2xl border border-gray-100 hover:border-indigo-200 hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1">
                  <div className="w-14 h-14 bg-gradient-to-r from-indigo-500 to-purple-500 rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
                    <Icon className="w-7 h-7 text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-4">
                    {feature.title}
                  </h3>
                  <p className="text-gray-600 leading-relaxed">
                    {feature.description}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Google AdSense Ad Middle - Rectangle */}
      <div className="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-center py-8">
          <AdSense 
            slot="9912805880"
            format="rectangle"
            style={{ display: 'block', width: '300px', height: '250px' }}
            className="w-full max-w-sm"
          />
        </div>
      </div>

      {/* How it works */}
      <section className="py-20 bg-gradient-to-br from-gray-50 to-indigo-50">
        <div className="max-w-6xl mx-auto px-4 text-center">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-16">
            {t('home.howItWorks.title')}
          </h2>
          
          <div className="grid md:grid-cols-3 gap-12">
            <div className="relative">
              <div className="w-20 h-20 bg-gradient-to-r from-indigo-500 to-purple-500 text-white rounded-2xl flex items-center justify-center text-2xl font-bold mx-auto mb-6 shadow-lg">
                1
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-4">
                {t('home.howItWorks.step1.title')}
              </h3>
              <p className="text-gray-600 leading-relaxed">
                {t('home.howItWorks.step1.desc')}
              </p>
            </div>
            
            <div className="relative">
              <div className="w-20 h-20 bg-gradient-to-r from-indigo-500 to-purple-500 text-white rounded-2xl flex items-center justify-center text-2xl font-bold mx-auto mb-6 shadow-lg">
                2
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-4">
                {t('home.howItWorks.step2.title')}
              </h3>
              <p className="text-gray-600 leading-relaxed">
                {t('home.howItWorks.step2.desc')}
              </p>
            </div>
            
            <div className="relative">
              <div className="w-20 h-20 bg-gradient-to-r from-indigo-500 to-purple-500 text-white rounded-2xl flex items-center justify-center text-2xl font-bold mx-auto mb-6 shadow-lg">
                3
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-4">
                {t('home.howItWorks.step3.title')}
              </h3>
              <p className="text-gray-600 leading-relaxed">
                {t('home.howItWorks.step3.desc')}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              {t('home.testimonials.title')}
            </h2>
            <p className="text-xl text-gray-600">
              {t('home.testimonials.subtitle')}
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <div key={index} className="bg-gradient-to-br from-gray-50 to-white p-8 rounded-2xl border border-gray-100 hover:shadow-lg transition-all duration-300">
                <div className="flex items-center mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <Star key={i} className="w-5 h-5 text-yellow-400 fill-current" />
                  ))}
                </div>
                <p className="text-gray-700 mb-6 italic leading-relaxed">
                  "{testimonial.content}"
                </p>
                <div className="flex items-center">
                  <div className="w-12 h-12 bg-gradient-to-r from-indigo-500 to-purple-500 rounded-full flex items-center justify-center text-white font-bold mr-4">
                    {testimonial.name.charAt(0)}
                  </div>
                  <div>
                    <p className="font-bold text-gray-900">{testimonial.name}</p>
                    <p className="text-sm text-gray-500">{testimonial.role}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-indigo-600 to-purple-600 rounded-3xl mx-4">
        <div className="max-w-5xl mx-auto px-4 text-center">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            {t('home.cta.title')}
          </h2>
          <p className="text-xl text-indigo-100 mb-10 max-w-3xl mx-auto">
            {t('home.cta.subtitle')}
          </p>
          <Link
            to="/register"
            className="inline-flex items-center bg-white text-indigo-600 px-10 py-4 rounded-xl font-bold hover:bg-gray-100 transition-all duration-300 transform hover:scale-105 shadow-lg"
          >
            {t('home.cta.button')}
            <ArrowRight className="ml-2 w-5 h-5" />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Home; 